import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldOff } from 'lucide-react';

/**
 * PilotClientRoute — blockiert Routen die für PILOT_CLIENT nicht erlaubt sind.
 * 
 * Erlaubte Routen für PILOT_CLIENT:
 *   / (dashboard), /properties, /tenants, /documents,
 *   /bewirtschaftung, /clara-ki, /clara-ai-search, /communication
 * 
 * Gesperrte Routen (Redirect auf /dashboard + 403-Anzeige):
 *   /banking, /finanzierungen, /steuern, /shared-economy,
 *   /investment-cockpit, /analytics, /lease-adjustments,
 *   /settings, /billing, /activities, /maintenance,
 *   /clara-iot, /property-visualizer, /marketplace,
 *   /regionale-insights, /vital-center, /owners, /hoa-management,
 *   /vps-dashboard, /prompt-vault, /manifest, /system-manifest,
 *   /contract-management, /tenant-portal, /suppliers
 */

// Routen die PILOT_CLIENT explizit erlaubt sind
const PILOT_CLIENT_ALLOWED_PATHS = new Set([
  '/',
  '/dashboard',
  '/properties',
  '/tenants',
  '/documents',
  '/bewirtschaftung',
  '/clara-ki',
  '/clara-ai-search',
  '/communication',
  '/communications',
  '/user-settings',  // eigene Profileinstellungen immer erlaubt
]);

// Route Guard für einzelne Routen
const PilotClientRoute = ({ children, redirectTo = '/dashboard' }) => {
  const { isLoading, isAuthenticated, rbacData, isPilotClient, isSuperAdmin, isAdmin, isManager, isClient } = useAuth();

  // Deep-Link-Schutz: Während die Sitzung bereits vorliegt, aber RBAC noch nicht
  // materialisiert ist, darf die Route nicht in den Default-Deny-Redirect fallen.
  if (isLoading || (isAuthenticated && !rbacData)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Berechtigungen werden geprüft…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // SUPER_ADMIN, ADMIN, MANAGER, CLIENT haben immer Zugriff
  if (isSuperAdmin || isAdmin || isManager || isClient) {
    return children;
  }

  // PILOT_CLIENT: blockiert
  if (isPilotClient) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="text-amber-400" size={28} />
          </div>
          <h2 className="text-white text-xl font-semibold mb-2">Bereich nicht verfügbar</h2>
          <p className="text-gray-400 text-sm mb-6">
            Dieser Bereich ist in Ihrem Pilotprofil nicht freigeschaltet.
            Bitte wenden Sie sich an Ihren Ansprechpartner.
          </p>
          <a
            href="/dashboard"
            className="inline-block bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            Zurück zur Übersicht
          </a>
        </div>
      </div>
    );
  }

  // Unbekannte Rolle: Default-Deny → Redirect
  return <Navigate to={redirectTo} replace />;
};

// Inline Guard für UI-Elemente (rendert null wenn kein Zugriff)
export const PilotClientOnly = ({ children }) => {
  const { isPilotClient } = useAuth();
  if (!isPilotClient) return null;
  return children;
};

// Inline Guard: rendert children NUR wenn NICHT PILOT_CLIENT
export const HideFromPilotClient = ({ children }) => {
  const { isPilotClient } = useAuth();
  if (isPilotClient) return null;
  return children;
};

export { PILOT_CLIENT_ALLOWED_PATHS };
export default PilotClientRoute;
